import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { useAuth } from '../contexts/AuthContext';
import { buildTabs, activeTabIndex } from '../nav/tabs';
import { getTierBadge, getSalesRole } from '../theme/tier';
import { colors, radii } from '../theme/tokens';
import { useTheme } from '../theme/ThemeContext';
import useIsDesktop from '../hooks/useIsDesktop';
import Icon from './ui/Icon';
import VoiceRecorder from './VoiceRecorder';

// --- Geometry --------------------------------------------------------------
// Full-bleed green bar. The active item is a detached green circle nested in a
// transparent, smoothly-rounded cutout carved into the bar (the page shows
// through it). The cutout is a Material "FAB cradle" path so its corners are
// rounded, not feathered or sharp.
const BAR_H = 60;
const NAV_H = 96;                  // room above the bar for the rising bubble
const BAR_TOP = NAV_H - BAR_H;     // top edge of the bar (container coords)
const D = 50;                      // detached bubble diameter
const REST_Y = BAR_TOP - 10;       // bubble centre when raised (nested); cutout follows
const SINK_Y = REST_Y + 10;        // bubble dips only ~10px during a move
const CUTOUT_R = 33;               // cradle radius (> bubble radius for a ring)

const ACCENT = colors.parGreen;            // bar + detached bubble
const ICON_INACTIVE = 'rgba(255,255,255,0.62)';
const ICON_ACTIVE = '#ffffff';

export const BUBBLE_NAV_HEIGHT = NAV_H;

// Port of Flutter's CircularNotchedRectangle: a bar rect with a rounded
// circular notch centred at `cx`. The quadratic corners + concave arc give the
// smooth rounded cutout the reference goo filter was approximating.
function notchPath(cx, W) {
  const top = BAR_TOP, bottom = NAV_H, left = 0, right = W;
  const gy = REST_Y, r = CUTOUT_R, s1 = 14, s2 = 1;
  const a = -r - s2;
  const b = top - gy;
  const n2 = Math.sqrt(Math.max(0, b * b * r * r * (a * a + b * b - r * r)));
  const p2xA = (a * r * r - n2) / (a * a + b * b);
  const p2xB = (a * r * r + n2) / (a * a + b * b);
  const p2yA = Math.sqrt(Math.max(0, r * r - p2xA * p2xA));
  const p2yB = Math.sqrt(Math.max(0, r * r - p2xB * p2xB));
  const cmp = b < 0 ? -1 : 1;
  const p2 = cmp * p2yA > cmp * p2yB ? [p2xA, p2yA] : [p2xB, p2yB];
  const p1 = [a, b];
  const p0 = [a - s1, b];
  const p3 = [-p2[0], p2[1]];
  const p4 = [-p1[0], p1[1]];
  const p5 = [-p0[0], p0[1]];
  const t = (p) => [p[0] + cx, p[1] + gy];
  const P0 = t(p0), P1 = t(p1), P2 = t(p2), P3 = t(p3), P4 = t(p4), P5 = t(p5);
  return (
    `M ${left} ${top} ` +
    `L ${P0[0].toFixed(2)} ${P0[1].toFixed(2)} ` +
    `Q ${P1[0].toFixed(2)} ${P1[1].toFixed(2)} ${P2[0].toFixed(2)} ${P2[1].toFixed(2)} ` +
    `A ${r} ${r} 0 0 0 ${P3[0].toFixed(2)} ${P3[1].toFixed(2)} ` +
    `Q ${P4[0].toFixed(2)} ${P4[1].toFixed(2)} ${P5[0].toFixed(2)} ${P5[1].toFixed(2)} ` +
    `L ${right} ${top} L ${right} ${bottom} L ${left} ${bottom} Z`
  );
}

const BubbleNav = () => {
  const {
    user,
    logout,
    isSalesTeam,
    canAccessInbox,
    canAccessNfc,
    canApprove,
    canAccessTasks,
    updateNotificationEmail,
    updateInstantEmails
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { mode, toggleTheme } = useTheme();

  // Sales team gets the nav; rubber users who manage Task Tracking also get it
  // even if they are not on the sales team.
  const showNav = isSalesTeam || canAccessTasks;
  const allTabs = showNav
    ? buildTabs({ canAccessInbox, canAccessNfc, canApprove, canAccessTasks })
    : [];
  // Bar shows the primary tabs; `menu` tabs (NFC, Admin) live in the profile
  // menu to keep the bar uncluttered.
  const tabs = allTabs.filter((t) => !t.menu);
  const menuTabs = allTabs.filter((t) => t.menu);

  const wrapRef = useRef(null);
  const pathRef = useRef(null);
  const bubbleRef = useRef(null);
  const iconRef = useRef(null);
  const lastX = useRef(0);
  const prevWidth = useRef(0);
  const mounted = useRef(false);
  const reduce = useRef(
    typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  const [width, setWidth] = useState(0);
  const [active, setActive] = useState(() => activeTabIndex(tabs, location.pathname));
  const [iconName, setIconName] = useState(tabs[active]?.icon);
  const [profileOpen, setProfileOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [emailInput, setEmailInput] = useState(user?.notificationEmail || '');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailMsg, setEmailMsg] = useState(null); // { ok, text } | null

  useEffect(() => {
    setEmailInput(user?.notificationEmail || '');
    setEmailMsg(null);
  }, [user?.notificationEmail]);

  const savedEmail = user?.notificationEmail || '';
  const emailDirty = emailInput.trim() !== savedEmail;
  const saveEmail = async () => {
    setEmailSaving(true);
    setEmailMsg(null);
    const res = await updateNotificationEmail(emailInput.trim());
    setEmailSaving(false);
    setEmailMsg(
      res.success
        ? { ok: true, text: emailInput.trim() ? 'Saved' : 'Cleared — using login email' }
        : { ok: false, text: res.error || 'Failed to save' }
    );
  };

  // Instant per-action email opt-in. Optimistically flips the switch, reverting
  // if the save fails. Scheduled digests are unaffected by this toggle.
  const instantEmails = user?.instantEmails === true;
  const [instantSaving, setInstantSaving] = useState(false);
  const toggleInstantEmails = async () => {
    if (instantSaving) return;
    setInstantSaving(true);
    await updateInstantEmails(!instantEmails);
    setInstantSaving(false);
  };

  useLayoutEffect(() => {
    if (!wrapRef.current) return;
    const el = wrapRef.current;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tabs.length]);

  useEffect(() => {
    setActive(activeTabIndex(tabs, location.pathname));
    setProfileOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, tabs.length]);

  // Profile sits as one extra slot after the route tabs.
  const slotCount = tabs.length + 1;
  const profileIndex = tabs.length;
  const slotW = width / (slotCount || 1);
  const slotX = (i) => slotW * (i + 0.5);

  const setPath = (cx) => {
    const path = pathRef.current;
    if (path && width) path.setAttribute('d', notchPath(cx, width));
  };

  useEffect(() => {
    const b = bubbleRef.current, ic = iconRef.current;
    if (!width || !pathRef.current || !b || !ic) return;
    const x = slotX(active);
    const nextIcon = tabs[active]?.icon;
    const resized = prevWidth.current !== width;
    prevWidth.current = width;

    if (!mounted.current || reduce.current || resized) {
      setPath(x);
      gsap.set([b, ic], { left: x });
      gsap.set(b, { top: REST_Y });
      gsap.set(ic, { top: REST_Y, scale: 1, opacity: 1 });
      setIconName(nextIcon);
      lastX.current = x;
      mounted.current = true;
      return;
    }

    const proxy = { x: lastX.current };
    const tl = gsap.timeline();
    tl.to(ic, { duration: 0.1, scale: 0, opacity: 0, ease: 'power2.in' }, 0)
      .to(b, { duration: 0.14, top: SINK_Y, ease: 'power2.in' }, 0)
      .add(() => setIconName(nextIcon), 0.12)
      .to(proxy, {
        duration: 0.34,
        x,
        ease: 'power3.inOut',
        onUpdate: () => {
          setPath(proxy.x);
          gsap.set([b, ic], { left: proxy.x });
        }
      }, 0.12)
      .to(b, { duration: 0.26, top: REST_Y, ease: 'back.out(1.7)' }, '-=0.12')
      .to(ic, { duration: 0.22, top: REST_Y, scale: 1, opacity: 1, ease: 'back.out(2)' }, '-=0.16');
    lastX.current = x;
    return () => tl.kill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, width]);

  if (!showNav || tabs.length === 0) return null;

  const go = (tab, i) => {
    setActive(i);
    navigate(tab.to);
  };

  const flip = isDesktop ? { transform: 'scaleY(-1)' } : undefined;
  const badge = getTierBadge(user?.tier);
  const role = getSalesRole(user?.salesRole);

  return (
    <>
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          [isDesktop ? 'top' : 'bottom']: 0,
          zIndex: 2000,
          pointerEvents: 'none'
        }}
        aria-label="Primary"
      >
        <nav
          ref={wrapRef}
          style={{
            position: 'relative',
            width: '100%',
            height: NAV_H,
            transform: isDesktop ? 'scaleY(-1)' : 'none',
            pointerEvents: 'auto'
          }}
        >
          {/* full-bleed green bar with a smooth transparent cutout */}
          <svg
            width="100%"
            height={NAV_H}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              overflow: 'visible',
              filter: 'drop-shadow(0 -3px 10px rgba(0,0,0,0.28))'
            }}
            aria-hidden="true"
          >
            <path ref={pathRef} fill={ACCENT} d="" />
          </svg>

          {/* detached green bubble nested in the cutout */}
          <div
            ref={bubbleRef}
            style={{
              position: 'absolute',
              left: 0,
              top: REST_Y,
              width: D,
              height: D,
              marginLeft: -D / 2,
              marginTop: -D / 2,
              borderRadius: '50%',
              background: ACCENT,
              boxShadow: '0 3px 10px rgba(0,0,0,0.35)',
              pointerEvents: 'none'
            }}
          />

          {/* active icon riding the bubble (same box as the bubble so it stays
              centred; GSAP animates left/top/scale around the box centre) */}
          <div
            ref={iconRef}
            style={{
              position: 'absolute',
              left: 0,
              top: REST_Y,
              width: D,
              height: D,
              marginLeft: -D / 2,
              marginTop: -D / 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none'
            }}
          >
            <Icon name={iconName} size={24} weight={700} color={ICON_ACTIVE} style={flip} />
          </div>

          {/* route tab hit-areas + inactive icons */}
          {tabs.map((tab, i) => (
            <button
              key={tab.key}
              onClick={() => go(tab, i)}
              title={tab.label}
              aria-label={tab.label}
              aria-current={i === active ? 'page' : undefined}
              style={{
                position: 'absolute',
                left: slotX(i),
                bottom: 0,
                transform: 'translateX(-50%)',
                width: slotW,
                height: BAR_H,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                opacity: i === active ? 0 : 1,
                transition: 'opacity 0.2s ease'
              }}
            >
              <Icon name={tab.icon} size={23} color={ICON_INACTIVE} style={flip} />
            </button>
          ))}

          {/* profile slot (avatar + tier ring) */}
          <button
            onClick={() => setProfileOpen((v) => !v)}
            title="Profile"
            aria-label="Profile"
            aria-haspopup="menu"
            aria-expanded={profileOpen}
            style={{
              position: 'absolute',
              left: slotX(profileIndex),
              bottom: 0,
              transform: 'translateX(-50%)',
              width: slotW,
              height: BAR_H,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            <span
              style={{
                ...(flip || {}),
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 34,
                height: 34,
                borderRadius: '50%',
                border: `2px solid ${badge.color}`,
                overflow: 'hidden',
                background: 'rgba(0,0,0,0.18)'
              }}
            >
              {user?.picture ? (
                <img src={user.picture} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <Icon name="person" size={20} color="#fff" />
              )}
            </span>
          </button>
        </nav>
      </div>

      {profileOpen && (
        <>
          <div
            onClick={() => setProfileOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 2050, background: 'transparent' }}
          />
          <div
            role="menu"
            style={{
              position: 'fixed',
              right: 12,
              [isDesktop ? 'top' : 'bottom']: BAR_H + 14,
              width: 248,
              zIndex: 2060,
              background: colors.glassStrong,
              border: `1px solid ${colors.glassBorder}`,
              borderRadius: radii.lg,
              boxShadow: '0 18px 40px rgba(0,0,0,0.5)',
              backdropFilter: 'blur(18px)',
              WebkitBackdropFilter: 'blur(18px)',
              padding: 14,
              color: colors.text
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: `2px solid ${badge.color}`,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0,0,0,0.18)',
                  flexShrink: 0
                }}
              >
                {user?.picture ? (
                  <img src={user.picture} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <Icon name="person" size={24} color="#fff" />
                )}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user?.name || 'Account'}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                  <Pill color={badge.color} icon={badge.icon} label={badge.label} dark />
                  {role && <Pill color={role.color} icon={role.icon} label={role.short} />}
                </div>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 10px',
                borderRadius: radii.md,
                background: 'rgba(47,182,92,0.12)',
                marginBottom: 10
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>
                <Icon name="paid" size={16} color={colors.parGreenBright} /> Tokens
              </span>
              <span style={{ fontWeight: 800, color: colors.parGreenBright }}>{user?.tokens || 0}</span>
            </div>

            <button
              onClick={toggleTheme}
              role="menuitemcheckbox"
              aria-checked={mode === 'light'}
              style={{
                width: '100%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                background: colors.glass,
                color: colors.text,
                border: `1px solid ${colors.glassBorder}`,
                padding: '10px 14px',
                borderRadius: radii.pill,
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 13,
                marginBottom: 8
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Icon name={mode === 'light' ? 'light_mode' : 'dark_mode'} size={18} />
                {mode === 'light' ? 'Light mode' : 'Dark mode'}
              </span>
              {/* little track + knob switch */}
              <span
                style={{
                  position: 'relative',
                  width: 38,
                  height: 22,
                  borderRadius: 999,
                  background: mode === 'light' ? colors.parGreen : 'rgba(128,128,128,0.4)',
                  transition: 'background 0.2s ease',
                  flexShrink: 0
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: 2,
                    left: mode === 'light' ? 18 : 2,
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: '#fff',
                    transition: 'left 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.4)'
                  }}
                />
              </span>
            </button>

            <div style={{ marginBottom: 10 }}>
              <label
                htmlFor="notif-email"
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: colors.textMuted, marginBottom: 6 }}
              >
                <Icon name="mail" size={15} color={colors.textMuted} /> Notification email
              </label>
              <input
                id="notif-email"
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder={user?.email || 'you@example.com'}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  background: colors.glass,
                  color: colors.text,
                  border: `1px solid ${colors.glassBorder}`,
                  borderRadius: radii.md,
                  padding: '9px 12px',
                  fontSize: 13,
                  marginBottom: 6
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span
                  style={{
                    fontSize: 11,
                    lineHeight: 1.3,
                    color: emailMsg ? (emailMsg.ok ? colors.parGreenBright : '#e5544b') : colors.textFaint
                  }}
                >
                  {emailMsg ? emailMsg.text : 'Blank = use your login email'}
                </span>
                <button
                  onClick={saveEmail}
                  disabled={emailSaving || !emailDirty}
                  style={{
                    flexShrink: 0,
                    background: emailDirty ? colors.parGreen : colors.glass,
                    color: emailDirty ? '#fff' : colors.textFaint,
                    border: `1px solid ${emailDirty ? colors.parGreen : colors.glassBorder}`,
                    padding: '6px 14px',
                    borderRadius: radii.pill,
                    cursor: emailSaving || !emailDirty ? 'default' : 'pointer',
                    fontWeight: 700,
                    fontSize: 12,
                    opacity: emailSaving ? 0.6 : 1
                  }}
                >
                  {emailSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>

            <button
              onClick={toggleInstantEmails}
              role="menuitemcheckbox"
              aria-checked={instantEmails}
              title="Email me the moment a task is assigned, you're @mentioned, or a lead is reassigned to you. Scheduled summary emails are always sent."
              style={{
                width: '100%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                background: colors.glass,
                color: colors.text,
                border: `1px solid ${colors.glassBorder}`,
                padding: '10px 14px',
                borderRadius: radii.pill,
                cursor: instantSaving ? 'default' : 'pointer',
                fontWeight: 700,
                fontSize: 13,
                marginBottom: 8,
                opacity: instantSaving ? 0.6 : 1
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, textAlign: 'left' }}>
                <Icon name="notifications_active" size={18} />
                Instant emails
              </span>
              {/* little track + knob switch (mirrors the theme toggle) */}
              <span
                style={{
                  position: 'relative',
                  width: 38,
                  height: 22,
                  borderRadius: 999,
                  background: instantEmails ? colors.parGreen : 'rgba(128,128,128,0.4)',
                  transition: 'background 0.2s ease',
                  flexShrink: 0
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: 2,
                    left: instantEmails ? 18 : 2,
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: '#fff',
                    transition: 'left 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.4)'
                  }}
                />
              </span>
            </button>
            <div style={{ fontSize: 11, lineHeight: 1.3, color: colors.textFaint, margin: '-2px 2px 10px' }}>
              Get an email for every action. Scheduled summaries are always sent.
            </div>

            {menuTabs.map((tab) => {
              const isActive = tab.match.some((m) =>
                m === '/' ? location.pathname === '/' : location.pathname === m || location.pathname.startsWith(m + '/')
              );
              return (
                <button
                  key={tab.key}
                  onClick={() => {
                    setProfileOpen(false);
                    navigate(tab.to);
                  }}
                  aria-current={isActive ? 'page' : undefined}
                  style={{
                    width: '100%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    background: isActive ? 'rgba(47,182,92,0.18)' : colors.glass,
                    color: isActive ? colors.parGreenBright : colors.text,
                    border: `1px solid ${isActive ? colors.parGreen : colors.glassBorder}`,
                    padding: '10px 14px',
                    borderRadius: radii.pill,
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: 13,
                    marginBottom: 8
                  }}
                >
                  <Icon name={tab.icon} size={18} color={isActive ? colors.parGreenBright : undefined} /> {tab.label}
                </button>
              );
            })}

            <button
              onClick={() => {
                setProfileOpen(false);
                setVoiceOpen(true);
              }}
              style={{
                width: '100%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                background: colors.parGreen,
                color: '#fff',
                border: 'none',
                padding: '10px 14px',
                borderRadius: radii.pill,
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 13,
                marginBottom: 8
              }}
            >
              <Icon name="mic" size={18} color="#fff" /> Voice note
            </button>

            <button
              onClick={logout}
              style={{
                width: '100%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                background: colors.glass,
                color: colors.text,
                border: `1px solid ${colors.glassBorder}`,
                padding: '10px 14px',
                borderRadius: radii.pill,
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 13
              }}
            >
              <Icon name="logout" size={18} /> Logout
            </button>
          </div>
        </>
      )}

      {voiceOpen && <VoiceRecorder onClose={() => setVoiceOpen(false)} />}
    </>
  );
};

const Pill = ({ color, icon, label, dark }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 3,
      fontSize: 10,
      fontWeight: 800,
      padding: '2px 7px',
      borderRadius: 999,
      background: color,
      color: dark ? '#1a1a1a' : '#fff'
    }}
  >
    <Icon name={icon} size={12} color={dark ? '#1a1a1a' : '#fff'} />
    {label}
  </span>
);

export default BubbleNav;
